import { createContext, useContext, useMemo, useState } from "react";
import {
  ApiIncrementalityControlGroupType,
  ApiIncrementalityTestPayload,
  ApiIncrementalityTestType,
} from "@incendium/api";

interface IIncrementalitySetupContext {
  incrementalityTest: ApiIncrementalityTestPayload;
  setIncrementalityTest: React.Dispatch<
    React.SetStateAction<ApiIncrementalityTestPayload>
  >;
  trendScore: number;
  setTrendScore: React.Dispatch<React.SetStateAction<number>>;
}

export const IncrementalitySetupContext =
  createContext<IIncrementalitySetupContext>({
    incrementalityTest: {
      name: "",
      type: ApiIncrementalityTestType.INCREMENTALITY_TEST_TYPE_GEO_SWITCH_OFF,
      controlGroupType:
        ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_AUTO_MATCHED,
      testFrom: undefined,
      testTo: undefined,
      preTestFrom: undefined,
      preTestTo: undefined,
      primaryMetric: undefined,
      supportingMetrics: [],
    },
    setIncrementalityTest: () => null,
    trendScore: 0,
    setTrendScore: () => null,
  });

export function useIncrementalitySetupContext() {
  const ctx = useContext(IncrementalitySetupContext);
  if (!ctx) {
    throw new Error(
      "useIncrementalitySetupContext must be inside IncrementalitySetupProvider",
    );
  }
  return ctx;
}

function IncrementalitySetupProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [incrementalityTest, setIncrementalityTest] =
    useState<ApiIncrementalityTestPayload>({
      name: "",
      type: ApiIncrementalityTestType.INCREMENTALITY_TEST_TYPE_GEO_SWITCH_OFF,
      controlGroupType:
        ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_AUTO_MATCHED,
      testFrom: undefined,
      testTo: undefined,
      preTestFrom: undefined,
      preTestTo: undefined,
      primaryMetric: undefined,
      supportingMetrics: [],
    });
  const [trendScore, setTrendScore] = useState(0);

  const state = useMemo(
    () => ({
      incrementalityTest,
      setIncrementalityTest,
      trendScore,
      setTrendScore,
    }),
    [incrementalityTest, trendScore],
  );

  return (
    <IncrementalitySetupContext.Provider value={state}>
      {children}
    </IncrementalitySetupContext.Provider>
  );
}

export default IncrementalitySetupProvider;
